<template>
    <div class="dbhome-page">
        <!-- 未登录提示 -->
        <div v-if="!isLogin" class="dbhome-notice no-select">
            <el-icon class="dbhome-notice__icon">
                <Coin />
            </el-icon>
            <span>登录后可执行共享的数据库 SQL</span>
        </div>

        <div class="dbhome-layout">
            <!-- 左侧：团队筛选 + 搜索 + 分组连接列表 -->
            <aside class="dbhome-side no-select">
                <div class="dbhome-side__toolbar">
                    <el-select v-model="selectedTeamId" class="dbhome-team" placeholder="全部团队" clearable
                        v-if="isLogin">
                        <el-option v-for="team in visibleTeamList" :key="team.id"
                            :label="`${team.name} 【${teamMatchCounts[team.id] || 0}】`" :value="team.id" />
                    </el-select>
                    <el-input v-model="searchKeyword" class="dbhome-search" placeholder="搜索连接名称或地址" clearable>
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>
                </div>

                <div class="dbhome-side__list">
                    <div class="dbhome-group" v-for="group in connGroupList" :key="group.key">
                        <div class="dbhome-group__head" v-if="showGroupHeader">
                            <span class="dbhome-group__name">{{ group.name }}</span>
                            <span class="dbhome-group__count">{{ group.list.length }} 个</span>
                        </div>
                        <ul class="dbhome-conns">
                            <li v-for="conn in group.list" :key="conn.id" class="dbhome-conn"
                                :class="{ 'is-active': activeTab === conn.id }" @click="openConn(conn)">
                                <span class="dbhome-conn__name" :title="conn.name">{{ conn.name }}</span>
                                <!-- i 标志：悬浮显示描述 + 只读团队 + 读写团队 -->
                                <el-tooltip placement="right" effect="light" :show-after="120">
                                    <template #content>
                                        <div class="dbhome-tip">
                                            <p class="dbhome-tip__desc">{{ conn.remark || '暂无描述' }}</p>
                                            <p>只读团队：{{ teamNames(conn.teamList) }}</p>
                                            <p>读写团队：{{ teamNames(conn.rwTeamList) }}</p>
                                        </div>
                                    </template>
                                    <el-icon class="dbhome-conn__info" @click.stop>
                                        <InfoFilled />
                                    </el-icon>
                                </el-tooltip>
                            </li>
                        </ul>
                    </div>

                    <div class="dbhome-side__empty" v-if="listLoaded && filteredList.length === 0">
                        <el-icon :size="22">
                            <Search />
                        </el-icon>
                        <p>{{ searchKeyword || selectedTeamId ? '没有匹配的连接' : (isLogin ? '暂无共享的数据库连接' : '登录后查看共享连接') }}</p>
                    </div>
                </div>
            </aside>

            <!-- 右侧：连接页签（每个连接一个独立的 SQL 控制台） -->
            <section class="dbhome-main">
                <el-tabs v-if="openTabs.length" v-model="activeTab" type="card" class="dbhome-tabs" closable
                    @tab-remove="closeTab">
                    <el-tab-pane v-for="c in openTabs" :key="c.id" :name="c.id" :label="c.name" lazy>
                        <db-console :conn="c" @changed="getShareList" />
                    </el-tab-pane>
                </el-tabs>
                <div v-else class="dbhome-main__empty">
                    <el-icon :size="26">
                        <Monitor />
                    </el-icon>
                    <p>从左侧选择一个数据库连接</p>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { ElMessageBox } from 'element-plus';
import request from '../../utils/request.js';
import DbConsole from './components/DbConsole.vue';

const UNGROUPED_KEY = '__ungrouped__';

export default {
    name: 'DataBaseMan',
    components: { DbConsole },
    data() {
        return {
            isLogin: false,
            searchKeyword: '',
            selectedTeamId: '',
            teamStorageKey: 'hidoc_dbman_selected_team_id',
            teamList: [],
            connList: [],
            listLoaded: false,
            // 已打开的连接（每个 = 一个页签 + 一个独立的控制台实例）
            openTabs: [],
            // 当前激活的页签（值为连接 id）
            activeTab: ''
        };
    },
    watch: {
        selectedTeamId(val) {
            localStorage.setItem(this.teamStorageKey, val || '');
        }
    },
    computed: {
        keywordFilteredList() {
            const list = this.connList;
            if (!this.searchKeyword) return list;
            const keywords = this.searchKeyword.trim().split(/\s+/).filter(k => k.length > 0);
            if (keywords.length === 0) return list;
            return list.filter(item => keywords.every(keyword => {
                const kw = keyword.toLowerCase();
                // 只搜连接本身（SQL 块明细在打开控制台时按需分页拉取，不在列表里）
                return (item.name || '').toLowerCase().includes(kw)
                    || (item.host || '').toLowerCase().includes(kw);
            }));
        },
        filteredList() {
            if (!this.selectedTeamId) return this.keywordFilteredList;
            return this.keywordFilteredList.filter(item =>
                (item.teamList || []).some(t => t.id === this.selectedTeamId)
                || (item.rwTeamList || []).some(t => t.id === this.selectedTeamId));
        },
        teamMatchCounts() {
            const counts = {};
            this.teamList.forEach(team => {
                counts[team.id] = 0;
            });
            this.keywordFilteredList.forEach(item => {
                const ids = [];
                (item.teamList || []).forEach(t => ids.push(t.id));
                (item.rwTeamList || []).forEach(t => ids.push(t.id));
                ids.forEach(id => {
                    if (counts[id] !== undefined) counts[id]++;
                });
            });
            return counts;
        },
        visibleTeamList() {
            return this.teamList.filter(team =>
                team.id === this.selectedTeamId || (this.teamMatchCounts[team.id] || 0) > 0
            );
        },
        connGroupList() {
            const groups = new Map();
            this.filteredList.forEach(conn => {
                const name = (conn.groupName || '').trim();
                const key = name || UNGROUPED_KEY;
                if (!groups.has(key)) {
                    groups.set(key, { key, name: name || '未分组', list: [] });
                }
                groups.get(key).list.push(conn);
            });
            const named = [];
            let ungrouped = null;
            groups.forEach(group => {
                if (group.key === UNGROUPED_KEY) ungrouped = group;
                else named.push(group);
            });
            named.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
            return ungrouped ? named.concat(ungrouped) : named;
        },
        showGroupHeader() {
            return this.connGroupList.some(group => group.key !== UNGROUPED_KEY);
        }
    },
    mounted() {
        document.title = 'Hidoc-数据库管理';
        this.isLogin = !!(this.$store.state.user.token);
        if (this.isLogin) {
            this.getTeamFilterOptions();
        }
        this.getShareList();
    },
    methods: {
        getShareList() {
            return request({
                url: '/dbMan/shareList',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.connList = res.data || [];
                    this.syncOpenTabs();
                }
                this.listLoaded = true;
            });
        },
        // 连接列表刷新后，同步已打开页签里的连接对象（保证控制台拿到最新数据）
        syncOpenTabs() {
            if (!this.openTabs.length) return;
            this.openTabs = this.openTabs.map(c => this.connList.find(x => x.id === c.id) || c);
        },
        getTeamFilterOptions() {
            return request({
                url: '/team/getMyJoinTeams',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0 && res.data) {
                    this.teamList = res.data.map(t => ({ id: t.id, name: t.name }))
                        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
                    const cached = localStorage.getItem(this.teamStorageKey);
                    if (cached && this.teamList.some(t => t.id === cached)) {
                        this.selectedTeamId = cached;
                    }
                }
            });
        },
        // 点左侧连接：打开或激活对应页签
        openConn(conn) {
            if (!this.isLogin) return;
            if (!this.openTabs.some(c => c.id === conn.id)) {
                this.openTabs.push(conn);
            }
            this.activeTab = conn.id;
        },
        // 关闭页签：先确认（未保存的 SQL 会丢失），取消则保留
        closeTab(id) {
            ElMessageBox.confirm('关闭后，该连接未保存的 SQL 语句将会丢失，确定关闭吗？', '关闭确认', {
                confirmButtonText: '确定关闭',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                const idx = this.openTabs.findIndex(c => c.id === id);
                if (idx === -1) return;
                this.openTabs.splice(idx, 1);
                if (this.activeTab === id) {
                    const next = this.openTabs[idx] || this.openTabs[idx - 1] || null;
                    this.activeTab = next ? next.id : '';
                }
            }).catch(() => { });
        },
        // 团队列表 → 展示文案
        teamNames(list) {
            const names = (list || []).map(t => t.name).filter(Boolean);
            return names.length ? names.join('、') : '无';
        }
    }
};
</script>

<style scoped>
.dbhome-page {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dbhome-text: #1f2d3d;
    --dbhome-text-sub: #5b6a80;
    --dbhome-text-weak: #8a94a6;
    --dbhome-line: #e6eaf0;

    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    color: var(--dbhome-text);
}

.dbhome-notice {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 10px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-size: 13px;
}

.dbhome-notice__icon {
    flex: 0 0 auto;
    font-size: 15px;
}

/* 左右两栏 */
.dbhome-layout {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    gap: 14px;
}

/* ---------- 左栏：连接列表 ---------- */
.dbhome-side {
    display: flex;
    flex-direction: column;
    flex: 0 0 280px;
    width: 280px;
    min-height: 0;
    border: 1px solid var(--dbhome-line);
    border-radius: 12px;
    background-color: #ffffff;
    overflow: hidden;
}

.dbhome-side__toolbar {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-bottom: 1px solid #eef1f6;
}

.dbhome-team,
.dbhome-search {
    width: 100%;
}

.dbhome-side__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 8px;
}

.dbhome-group {
    margin-bottom: 10px;
}

/* 分组分栏（滚动时悬浮）——样式参照 ssh/index.vue 的 .ssh-group__head */
.dbhome-group__head {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary-light-7);
    border-left: 3px solid var(--el-color-primary);
    border-radius: 8px;
    background: linear-gradient(90deg, var(--el-color-primary-light-8) 0%, var(--el-color-primary-light-9) 100%);
    box-shadow: 0 8px 16px -14px rgba(47, 111, 237, 0.9);
}

.dbhome-group__name {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: var(--el-color-primary-dark-2);
}

.dbhome-group__count {
    padding: 2px 8px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 999px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
}

.dbhome-conns {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
}

.dbhome-conn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 8px;
    border-radius: 8px;
    color: var(--dbhome-text);
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;
}

.dbhome-conn:hover {
    background-color: #f2f6fd;
}

.dbhome-conn.is-active {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-weight: 600;
}

.dbhome-conn__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbhome-conn__info {
    flex: 0 0 auto;
    color: #b3bcc9;
}

.dbhome-conn__info:hover {
    color: var(--el-color-primary);
}

.dbhome-tip {
    max-width: 260px;
}

.dbhome-tip p {
    margin: 2px 0;
    font-size: 12px;
    line-height: 1.5;
}

.dbhome-tip__desc {
    color: #1f2d3d;
    font-weight: 600;
}

.dbhome-side__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 12px;
    color: #909399;
    font-size: 13px;
    text-align: center;
}

.dbhome-side__empty p {
    margin: 0;
}

/* ---------- 右栏：连接页签 ---------- */
.dbhome-main {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.dbhome-tabs {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbhome-tabs :deep(.el-tabs__header) {
    flex: 0 0 auto;
    margin: 0 0 10px;
}

.dbhome-tabs :deep(.el-tabs__content) {
    flex: 1 1 auto;
    min-height: 0;
    /* 必须放开 overflow，否则内部 sticky / 弹层会被裁掉 */
    overflow: visible;
}

.dbhome-tabs :deep(.el-tab-pane) {
    height: 100%;
}

.dbhome-main__empty {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #909399;
    font-size: 13px;
}

.dbhome-main__empty p {
    margin: 0;
}

@media (max-width: 768px) {
    .dbhome-layout {
        flex-direction: column;
    }

    .dbhome-side {
        flex: 0 0 auto;
        width: auto;
        max-height: 40vh;
    }
}
</style>
